import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Tree } from "@minoru/react-dnd-treeview";
import {
	DragLayerMonitorProps,
	DropOptions,
	NodeModel,
} from "@minoru/react-dnd-treeview/dist/types";
import { Box, Grid2 } from "@mui/material";
import {
	MouseEvent as ReactMouseEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useConfigurationDelayCodeContext } from "../../context/ConfigurationDelayCodeContext";
import { useDelayActions } from "../../hooks/delays/useDelayActions";
import { useSearchDelays } from "../../hooks/delays/useSearchDelays";
import { useDelayCodeOptionsFunctions } from "../../hooks/useDelayCodeOptionsFunctions";
import { CatalogDelayCode } from "../../models/CatalogDelayCode";
import { FileProperties } from "../../models/FileProperties";
import { TreeParameters } from "../../models/TreeParameter";
import { moveDelayCode } from "../../repositories/ConfigurationDelayRepository";
import { CustomNode } from "../CustomNode";
import { CustomDragPreview } from "../DragPreview/CustomDragPreview";
import { MultipleDragPreview } from "../DragPreview/MultipleDragPreview";

interface TreeViewDelayProps {
	delayAreaAssetId?: number;
	delayAreaAssetName?: string;
	onNodeSelect: (node: CatalogDelayCode) => void;
	onRequestAdd: () => void;
	onRequestDelete: (node: CatalogDelayCode) => void;
	onToggleQuickButton: (node: CatalogDelayCode) => void;
	onToggleActive: (node: CatalogDelayCode) => void;
	onError: (msg: string) => void;
	onNotification: (msg: string) => void;
	scrollToNodeId: number | null;
	onlyActives: boolean;
}

export const TreeViewDelay = ({
	delayAreaAssetId,
	delayAreaAssetName,
	onNodeSelect,
	onRequestAdd,
	onRequestDelete,
	onToggleQuickButton,
	onToggleActive,
	onError,
	onNotification,
	scrollToNodeId,
	onlyActives,
}: TreeViewDelayProps) => {
	const [selectedNodes, setSelectedNodes] = useState<
		NodeModel<FileProperties>[]
	>([]);
	const [isDragging, setIsDragging] = useState(false);
	const [isCtrlPressing, setIsCtrlPressing] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	const {
		state: { allDelayNodes, allDelayCodes, openDelays },
		actions: { setDelayNodeSelectedInTree, setDelayAreaAsset, setOpenDelays },
	} = useConfigurationDelayCodeContext();

	const { showContextMenu, registerConfig } = useContextMenuMESF();

	const { searchDelays } = useSearchDelays({
		setSearchError: onError,
		setAllDelayLoading: setIsLoading,
		includeInactive: true,
	});

	// Scroll to node and select it when search result is clicked
	useEffect(() => {
		if (!scrollToNodeId) return;
		// Expand ancestors so the node is visible
		const nodeMap = new Map(allDelayNodes.map((n) => [n.id, n]));
		const newOpen = { ...openDelays };
		let current = nodeMap.get(scrollToNodeId);
		while (current && current.parent !== 0) {
			newOpen[current.parent] = current.parent;
			current = nodeMap.get(current.parent);
		}
		setOpenDelays(newOpen);
		// Select the node in the tree (highlight)
		const targetNode = nodeMap.get(scrollToNodeId);
		if (targetNode) {
			setSelectedNodes([targetNode]);
		}
		// Scroll after tree expands
		setTimeout(() => {
			const el = document.querySelector(`[data-node-id="${scrollToNodeId}"]`);
			if (el) {
				el.scrollIntoView({ behavior: "smooth", block: "center" });
			}
		}, 300);
	}, [scrollToNodeId]);

	// Memoized sort — stable reference prevents Tree library from re-sorting on every render
	const sortBySortOrder = useCallback(
		(a: NodeModel<FileProperties>, b: NodeModel<FileProperties>): number => {
			const nodeA = a as unknown as TreeParameters;
			const nodeB = b as unknown as TreeParameters;
			const sortA = nodeA.sortOrder;
			const sortB = nodeB.sortOrder;
			if (sortA === undefined || sortA === null) {
				if (sortB === undefined || sortB === null) return 0;
				return -1;
			}
			if (sortB === undefined || sortB === null) return 1;
			return sortA - sortB;
		},
		[],
	);

	// O(1) lookup map for allDelayCodes — avoids linear .find() calls on 1441 items
	const delayCodesMap = useMemo(() => {
		const codes = Array.isArray(allDelayCodes) ? allDelayCodes : [];
		return new Map(
			codes
				.filter((c: CatalogDelayCode) => c.DelayCodeId !== null)
				.map((c: CatalogDelayCode) => [c.DelayCodeId as number, c]),
		);
	}, [allDelayCodes]);

	const { handleDrop, handleCtrlClick, handleDragStart, handleDragEnd } =
		useDelayActions({
			delay: allDelayNodes,
			selectedNodes,
			setSelectedNodes,
			setIsDragging,
			isCtrlPressing,
			setIsCtrlPressing,
			onDelayUpdateStart: () => {
				setIsLoading(true);
			},
			onDelayUpdateEnd: (result) => {
				if (result.ok) {
					onNotification("The delay was reordered successfully");
				} else {
					onError(result.message);
				}
				searchDelays(delayAreaAssetId);
			},
		});

	const handleMoveUp = async ({ delayCodeId }: { delayCodeId: number }) => {
		if (!delayCodeId) return;
		try {
			const result = await moveDelayCode(delayCodeId, false);
			if (result.ok) {
				onNotification("The delay was moved up successfully");
			} else {
				onError(result.message);
			}
			await searchDelays(delayAreaAssetId);
		} catch {
			onError("An error occurred while moving the delay code.");
		}
	};

	const handleMoveDown = async ({ delayCodeId }: { delayCodeId: number }) => {
		if (!delayCodeId) return;
		try {
			const result = await moveDelayCode(delayCodeId, true);
			if (result.ok) {
				onNotification("The delay was moved down successfully");
			} else {
				onError(result.message);
			}
			await searchDelays(delayAreaAssetId);
		} catch {
			onError("An error occurred while moving the delay code.");
		}
	};

	const handleNodeSelect = (treeNode: TreeParameters) => {
		const catalogNode = delayCodesMap.get(treeNode.id as number);
		if (catalogNode) {
			setDelayNodeSelectedInTree(catalogNode);
			onNodeSelect(catalogNode);
		}
	};

	const handleRequestDelete = (treeNode: TreeParameters) => {
		const catalogNode = delayCodesMap.get(treeNode.id as number);
		if (catalogNode) {
			setDelayNodeSelectedInTree(catalogNode);
			onRequestDelete(catalogNode);
		}
	};

	const handleToggleQuickButton = (treeNode: TreeParameters) => {
		const catalogNode = delayCodesMap.get(treeNode.id as number);
		if (catalogNode) {
			onToggleQuickButton(catalogNode);
		}
	};

	const handleToggleActive = (treeNode: TreeParameters) => {
		const catalogNode = delayCodesMap.get(treeNode.id as number);
		if (catalogNode) {
			onToggleActive(catalogNode);
		}
	};

	const { getMenuOptions } = useDelayCodeOptionsFunctions({
		setDelayNodeSelectedInTree,
		onNodeSelect: handleNodeSelect,
		onRequestAdd,
		onRequestDelete: handleRequestDelete,
		onToggleQuickButton: handleToggleQuickButton,
		onToggleActive: handleToggleActive,
		handleMoveUp,
		handleMoveDown,
	});

	// Use ref to keep getMenuOptions always fresh without re-registering
	const getMenuOptionsRef = useRef(getMenuOptions);
	getMenuOptionsRef.current = getMenuOptions;

	const stableGetMenuOptions = useCallback(
		(data: any) => getMenuOptionsRef.current(data),
		[],
	);

	const handleContextMenu = useCallback(
		(data: NodeModel<FileProperties>, e: ReactMouseEvent<HTMLElement>) => {
			e.preventDefault();
			if (e.type === "contextmenu") {
				setSelectedNodes([data]);
				showContextMenu(e, data, "delayCodeContext");
				const selectedNode = delayCodesMap.get(data.id as number);
				if (selectedNode) {
					setDelayNodeSelectedInTree(selectedNode);
				}
			}
		},
		[delayCodesMap, showContextMenu, setDelayNodeSelectedInTree],
	);

	const handleNodeClick = useCallback(
		(e: ReactMouseEvent, node: NodeModel<FileProperties>) => {
			handleCtrlClick(e, node);
			if (!e.ctrlKey && !e.metaKey) {
				const catalogNode = delayCodesMap.get(node.id as number);
				if (catalogNode) {
					setDelayNodeSelectedInTree(catalogNode);
					onNodeSelect(catalogNode);
				}
			}
		},
		[delayCodesMap, handleCtrlClick, setDelayNodeSelectedInTree, onNodeSelect],
	);

	const handleNodeDoubleClick = useCallback(
		(data: NodeModel<FileProperties>, _e: ReactMouseEvent<HTMLElement>) => {
			const catalogNode = delayCodesMap.get(data.id as number);
			if (catalogNode) {
				setDelayNodeSelectedInTree(catalogNode);
				onNodeSelect(catalogNode);
			}
		},
		[delayCodesMap, setDelayNodeSelectedInTree, onNodeSelect],
	);

	// Stable callback for CustomNode to toggle open/close — avoids context subscription in CustomNode
	// Use ref so the callback never changes reference, preventing ALL CustomNodes from re-rendering on toggle
	const openDelaysRef = useRef(openDelays);
	openDelaysRef.current = openDelays;

	const handleToggleOpen = useCallback(
		(nodeId: number | string) => {
			const current = openDelaysRef.current;
			if (current[nodeId]) {
				const next = { ...current };
				delete next[nodeId as number];
				setOpenDelays(next);
			} else {
				setOpenDelays({ ...current, [nodeId]: nodeId });
			}
		},
		[setOpenDelays],
	);

	const openDelaysArray = useMemo(() => {
		return Object.keys(openDelays).map((el) => parseInt(el));
	}, [openDelays]);

	// A deactivated branch takes its subtree with it: an active child under an
	// inactive parent cannot be reached through the tree anyway.
	const activeNodes = useMemo(() => {
		if (!onlyActives) return allDelayNodes;
		const byId = new Map(allDelayNodes.map((n) => [n.id, n]));
		return allDelayNodes.filter((n) => {
			// A delay code has already been its own parent once in this database,
			// so the walk up refuses to visit the same node twice.
			const seen = new Set<number>();
			let current: (typeof allDelayNodes)[number] | undefined = n;
			while (current && !seen.has(current.id)) {
				if (current.isActive === false) return false;
				if (current.parent === 0) return true;
				seen.add(current.id);
				current = byId.get(current.parent);
			}
			return true;
		});
	}, [allDelayNodes, onlyActives]);

	// Filter tree data to only include visible nodes — drastically reduces DOM elements
	// The library creates wrapper <li> + DnD elements per node, so passing all 1441 nodes
	// creates ~5000+ DOM elements. Filtering to ~50-200 visible nodes fixes performance.
	const filteredTree = useMemo(() => {
		if (activeNodes.length === 0) return activeNodes;
		const openSet = new Set(openDelaysArray);
		const parentMap = new Map<number | string, number | string>();
		for (const n of activeNodes) {
			parentMap.set(n.id, n.parent);
		}
		return activeNodes.filter((n) => {
			// Root-level nodes always included
			if (n.parent === 0) return true;
			// Include only if ALL ancestors up to root are open
			let pid: number | string = n.parent;
			while (pid !== 0) {
				if (!openSet.has(pid as number)) return false;
				const next = parentMap.get(pid);
				pid = next !== undefined ? next : 0;
			}
			return true;
		});
	}, [activeNodes, openDelaysArray]);

	useEffect(() => {
		registerConfig({
			id: "delayCodeContext",
			getOptions: stableGetMenuOptions,
		});
	}, []);

	// Auto-expand root level when data first loads
	useEffect(() => {
		if (allDelayNodes.length > 0 && Object.keys(openDelays).length === 0) {
			const initial: Record<number | string, number | string> = {};
			for (const n of allDelayNodes) {
				if (n.parent === 0) {
					initial[n.id] = n.id;
				}
			}
			if (Object.keys(initial).length > 0) {
				setOpenDelays(initial);
			}
		}
	}, [allDelayNodes]);

	useEffect(() => {
		const delayAreaAsset = {
			AssetId: delayAreaAssetId,
			AssetName: delayAreaAssetName,
		};
		setDelayAreaAsset(delayAreaAsset);
		searchDelays(delayAreaAssetId);
	}, [delayAreaAssetId]);

	if (isLoading) {
		return <CenteredLazyLoading />;
	}

	return (
		<DndProvider backend={HTML5Backend}>
			<Grid2
				container
				onContextMenu={(e) => {
					e.preventDefault();
					showContextMenu(e, null, "delayCodeContext");
				}}
				sx={{ height: "100%" }}
			>
				<Box
					sx={{
						height: "100%",
						width: "100%",
						overflowY: "auto",
						paddingBottom: 2,
						"& ul, & ol": {
							listStyleType: "none",
							margin: 0,
							padding: 0,
						},
						"& .root": {
							boxSizing: "border-box",
							height: "100%",
							padding: 2,
						},
						"& .dropTarget": {
							boxShadow: "0 0 0 1px #1967d2 inset",
						},
					}}
				>
					<Tree
						rootId={0}
						initialOpen={openDelaysArray}
						tree={filteredTree}
						sort={sortBySortOrder}
						insertDroppableFirst={false}
						enableAnimateExpand={false}
						classes={{
							root: "root",
							dropTarget: "dropTarget",
						}}
						onDrop={handleDrop}
						onDragStart={handleDragStart}
						onDragEnd={handleDragEnd}
						canDrop={(
							_dcode: NodeModel<FileProperties>[],
							options: DropOptions<FileProperties>,
						): boolean => {
							if (!(options?.dropTarget as TreeParameters)?.isEditable) {
								return false;
							}
							if (
								selectedNodes.some(
									(selectedNode) =>
										!(selectedNode as TreeParameters)?.isEditable,
								)
							) {
								return false;
							}
							return !selectedNodes.some(
								(selectedNode) => selectedNode.id === options.dropTargetId,
							);
						}}
						render={(node, options) => {
							const selected = selectedNodes.some(
								(selectedNode) => selectedNode.id === node.id,
							);
							return (
								<CustomNode
									setContextMenuOver={handleContextMenu}
									node={node}
									{...options}
									isSelected={selected}
									isDragging={selected && isDragging}
									onClick={handleNodeClick}
									onDoubleClick={handleNodeDoubleClick}
									onToggleOpen={handleToggleOpen}
								/>
							);
						}}
						dragPreviewRender={(
							monitorProps: DragLayerMonitorProps<FileProperties>,
						) => {
							if (selectedNodes.length > 1) {
								return <MultipleDragPreview dragSources={selectedNodes} />;
							}
							return <CustomDragPreview monitorProps={monitorProps} />;
						}}
					/>
				</Box>
			</Grid2>
		</DndProvider>
	);
};
